/**
 * useBounties - TanStack Query hooks for bounty/task data
 * 支持用户状态变化时的自动重新获取
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { getBrowserSupabaseClient } from "@/lib/supabase/client";
import { queryKeys } from "@/lib/query-client";
import type { Bounty } from "@/lib/supabase/types";
import {
  claimBounty as repoClaimBounty,
  abandonBounty as repoAbandonBounty,
  updateBountyStatus as repoUpdateBountyStatus,
} from "@/repositories/bounties.repository";
import {
  submitDeliveryV2,
  requestDeliveryModification,
  settleBounty as rpcSettleBounty,
  refundBounty as rpcRefundBounty,
} from "@/lib/supabase/rpc";

// ========== Hall Tasks Query ==========

interface UseHallBountiesOptions {
  category?: string;
  search?: string;
  sort?: "default" | "amount" | "deadline";
  page?: number;
  pageSize?: number;
}

interface HallBountyItem {
  id: string;
  title: string;
  amount: string;
  level: string;
  slots: number;
  deadline: string;
  tags: string[];
}

function formatRewardAmount(rewardBbp: number): string {
  return `¥${rewardBbp.toLocaleString("zh-CN")}`;
}

function deriveLevel(rewardBbp: number): string {
  if (rewardBbp < 1000) return "L0+";
  if (rewardBbp < 3000) return "L1+";
  if (rewardBbp < 5000) return "L2+";
  if (rewardBbp < 10000) return "L3+";
  if (rewardBbp < 20000) return "L4+";
  return "L5+";
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "无截止日期";
  return dateStr.slice(0, 10);
}

export function useHallBounties(options: UseHallBountiesOptions = {}) {
  const { category, search, sort = "default", page = 0, pageSize = 10 } = options;

  return useQuery({
    queryKey: queryKeys.bounties.hall({ category, search, sort }),
    queryFn: async (): Promise<{ items: HallBountyItem[]; total: number }> => {
      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");

      const from = page * pageSize;
      const to = from + pageSize - 1;

      let query = supabase
        .from("bounties")
        .select("id, title, reward_usdc, bidding_deadline, tech_tags, category, status", {
          count: "exact",
        })
        .eq("status", "bidding")
        .order("created_at", { ascending: false })
        .range(from, to);

      // Category filter
      if (category && category !== "全部") {
        query = query.eq("category", category);
      }

      // Search filter
      if (search?.trim()) {
        query = query.ilike("title", `%${search.trim()}%`);
      }

      // Sorting
      if (sort === "amount") {
        query = query.order("reward_usdc", { ascending: false });
      } else if (sort === "deadline") {
        query = query.order("bidding_deadline", { ascending: true });
      }

      const { data, error, count } = await query;

      if (error) throw new Error(error.message);

      const items: HallBountyItem[] = (data ?? []).map((b) => ({
        id: b.id,
        title: b.title,
        amount: formatRewardAmount(b.reward_usdc),
        level: deriveLevel(b.reward_usdc),
        slots: b.status === "bidding" ? 1 : 0,
        deadline: formatDate(b.bidding_deadline),
        tags: b.tech_tags ?? [],
      }));

      return { items, total: count ?? 0 };
    },
    placeholderData: (previousData) => previousData,
    staleTime: 30 * 1000, // 30 秒内认为数据新鲜
  });
}

// ========== My Tasks Query ==========

interface MyBountyItem {
  id: string;
  title: string;
  progress: number;
  status: "待开始" | "评审中" | "进行中" | "待验收" | "已完成" | "已逾期" | "已取消";
  deadline: string;
  isOverdue: boolean;
  role: "publisher" | "engineer";
}

function mapBountyToMyTaskStatus(bounty: Bounty): {
  status: MyBountyItem["status"];
  progress: number;
  isOverdue: boolean;
} {
  const now = new Date();
  const deadlineDate = bounty.completion_deadline ? new Date(bounty.completion_deadline) : null;
  const isOverdue = deadlineDate ? now > deadlineDate : false;

  switch (bounty.status) {
    case "reviewing":
      return { status: "评审中", progress: 20, isOverdue };
    case "in_progress":
      return { status: "进行中", progress: 50, isOverdue };
    case "completed":
      return { status: "已完成", progress: 100, isOverdue: false };
    case "cancelled":
      return { status: "已取消", progress: 0, isOverdue };
    default:
      return { status: "待开始", progress: 0, isOverdue };
  }
}

interface UseMyBountiesOptions {
  userId?: string | null | undefined; // 关键：null/undefined 时返回空
  page?: number;
  pageSize?: number;
}

export function useMyBounties(options: UseMyBountiesOptions = {}) {
  const { userId, page = 0, pageSize = 10 } = options;

  return useQuery({
    // userId 变化时自动重新获取
    queryKey: [...queryKeys.bounties.my(userId ?? ""), { page, pageSize }],
    queryFn: async (): Promise<{ items: MyBountyItem[]; total: number }> => {
      // 未登录时不发送请求
      if (!userId) return { items: [], total: 0 };

      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");

      const from = page * pageSize;
      const to = from + pageSize - 1;

      const { data, error, count } = await supabase
        .from("bounties")
        .select("id, title, status, publisher_id", { count: "exact" })
        .or(`claimed_by.eq.${userId},publisher_id.eq.${userId}`)
        .neq("status", "drafting")
        .neq("status", "bidding")
        .order("updated_at", { ascending: false })
        .range(from, to);

      if (error) throw new Error(error.message);

      const items: MyBountyItem[] = (data ?? []).map((b) => {
        const mapped = mapBountyToMyTaskStatus(b as Bounty);
        const record = b as { publisher_id?: string };
        return {
          id: b.id,
          title: b.title,
          progress: mapped.progress,
          status: mapped.status,
          deadline: "-",
          isOverdue: mapped.isOverdue,
          role: record.publisher_id === userId ? "publisher" as const : "engineer" as const,
        };
      });

      return { items, total: count ?? 0 };
    },
    // 未登录时不执行查询
    enabled: !!userId,
    staleTime: 30 * 1000,
  });
}

// ========== Bounty Detail Query ==========

export function useBountyDetail(bountyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.bounties.detail(bountyId ?? ""),
    queryFn: async (): Promise<Bounty | null> => {
      if (!bountyId) return null;

      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");

      const { data, error } = await supabase
        .from("bounties")
        .select("*")
        .eq("id", bountyId)
        .single();

      if (error) throw new Error(error.message);
      return data as Bounty;
    },
    enabled: !!bountyId,
    staleTime: 60 * 1000, // 详情页缓存 1 分钟
  });
}

// ========== Cache Invalidation Hook ==========

/**
 * 获取 queryClient 实例和相关方法
 * 用于在数据变更后主动 invalidation
 */
export function useBountyQueryUtils() {
  const queryClient = useQueryClient();

  const invalidateHall = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.bounties.all });
  }, [queryClient]);

  const invalidateMyTasks = useCallback(
    (userId: string) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bounties.my(userId) });
    },
    [queryClient],
  );

  const invalidateBountyDetail = useCallback(
    (bountyId: string) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bounties.detail(bountyId) });
    },
    [queryClient],
  );

  return {
    invalidateHall,
    invalidateMyTasks,
    invalidateBountyDetail,
  };
}

// ========== Mutation Hooks ==========

/** 认领任务 */
export function useClaimBounty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      bountyId,
      engineerId,
    }: {
      bountyId: string;
      engineerId: string;
    }) => {
      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");
      return repoClaimBounty(supabase, bountyId, engineerId);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bounties.all });
      queryClient.invalidateQueries({
        queryKey: queryKeys.bounties.detail(variables.bountyId),
      });
    },
  });
}

/** 提交交付 */
export function useSubmitDelivery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      bountyId,
      content,
      links,
    }: {
      bountyId: string;
      content: string;
      links?: string[];
    }) => {
      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");
      return submitDeliveryV2(supabase, bountyId, content, links);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.bounties.detail(variables.bountyId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.bounties.all });
    },
  });
}

/** 放弃任务 */
export function useAbandonBounty() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      bountyId,
      engineerId,
    }: {
      bountyId: string;
      engineerId: string;
    }) => {
      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");
      return repoAbandonBounty(supabase, bountyId, engineerId);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.bounties.detail(variables.bountyId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.bounties.all });
    },
  });
}

/** 验收交付（发布方确认 → 结算 BBP） */
export function useAcceptDelivery() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      bountyId,
    }: {
      bountyId: string;
    }) => {
      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");
      // 1. 状态机校验 + 更新为 completed
      const result = await repoUpdateBountyStatus(supabase, bountyId, "completed");
      if (!result.success) throw new Error(result.error ?? "验收失败");
      // 2. BBP 结算（90% 工程师 + 10% 平台）
      await rpcSettleBounty(supabase, bountyId);
      return result;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.bounties.detail(variables.bountyId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.bounties.all });
    },
  });
}

/** 要求修改交付 */
export function useRequestModification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      deliveryId,
      bountyId,
      reviewNote,
    }: {
      deliveryId: string;
      bountyId: string;
      reviewNote?: string;
    }) => {
      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");
      return requestDeliveryModification(supabase, deliveryId, bountyId, reviewNote);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.bounties.detail(variables.bountyId),
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.bounties.all });
    },
  });
}

// ========== 竞标 Hooks ==========

/** 获取任务的所有竞标方案（发布方/管理员视角） */
export function useBidsForBounty(bountyId: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.bounties.detail(bountyId ?? ""), "bids"],
    queryFn: async () => {
      if (!bountyId) return [];
      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");
      const { listBidsForBounty } = await import("@/repositories/bounties.repository");
      return listBidsForBounty(supabase, bountyId);
    },
    enabled: !!bountyId,
    staleTime: 30 * 1000,
  });
}

/** 获取工程师在指定任务的竞标方案 */
export function useMyBid(bountyId: string | undefined, userId: string | null | undefined) {
  return useQuery({
    queryKey: [...queryKeys.bounties.detail(bountyId ?? ""), "myBid", userId ?? ""],
    queryFn: async () => {
      if (!bountyId || !userId) return null;
      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");
      const { getMyBidForBounty } = await import("@/repositories/bounties.repository");
      return getMyBidForBounty(supabase, bountyId, userId);
    },
    enabled: !!bountyId && !!userId,
    staleTime: 30 * 1000,
  });
}

/** 提交竞标方案 */
export function useSubmitBid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: {
      bountyId: string;
      engineerId: string;
      proposal: string;
      approach: string;
      estimatedDays: number;
      proposedReward: number;
      relevantExperience: string;
      milestonePlan?: string;
      portfolioLinks?: string[];
      coverLetter?: string;
    }) => {
      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");
      const { submitBid } = await import("@/lib/supabase/rpc");
      const result = await submitBid(
        supabase,
        input.bountyId,
        input.engineerId,
        input.proposal,
        input.approach,
        input.estimatedDays,
        input.proposedReward,
        input.relevantExperience,
        input.milestonePlan,
        input.portfolioLinks,
        input.coverLetter,
      );
      if (!result.success) {
        throw new Error(result.error ?? "提交方案失败");
      }
      return result;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bounties.detail(variables.bountyId) });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.bounties.detail(variables.bountyId), "bids"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.bounties.all });
    },
  });
}

/** 撤回竞标方案 */
export function useWithdrawBid() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bidId, engineerId, bountyId }: { bidId: string; engineerId: string; bountyId: string }) => {
      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");
      const { withdrawBid } = await import("@/repositories/bounties.repository");
      return withdrawBid(supabase, bidId, engineerId);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bounties.detail(variables.bountyId) });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.bounties.detail(variables.bountyId), "bids"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.bounties.all });
    },
  });
}

/** 发布方选择中标工程师 */
export function useSelectBidder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bountyId, bidId, publisherId }: { bountyId: string; bidId: string; publisherId: string }) => {
      const supabase = getBrowserSupabaseClient();
      if (!supabase) throw new Error("Supabase 未配置");
      const { selectBidder } = await import("@/lib/supabase/rpc");
      const result = await selectBidder(supabase, bountyId, bidId, publisherId);
      if (!result.success) {
        throw new Error(result.error ?? "选择中标方案失败");
      }
      return result;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.bounties.detail(variables.bountyId) });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.bounties.detail(variables.bountyId), "bids"] });
      queryClient.invalidateQueries({ queryKey: queryKeys.bounties.all });
    },
  });
}

// ========== 资格检查 Hook ==========

export interface ClaimEligibility {
  canClaim: boolean;
  reasons: string[];
}

/** 检查用户是否有资格竞标指定任务 */
export function useClaimEligibility(
  bountyId: string | undefined,
  userId: string | null | undefined,
  bountyStatus: string | undefined,
  publisherId: string | null | undefined,
  biddingDeadline: string | null | undefined,
  maxBidders: number | undefined,
  bidCount: number | undefined,
) {
  return useQuery({
    queryKey: [...queryKeys.bounties.detail(bountyId ?? ""), "eligibility", userId ?? ""],
    queryFn: async (): Promise<ClaimEligibility> => {
      const reasons: string[] = [];

      if (!userId) {
        return { canClaim: false, reasons: ["请先登录"] };
      }

      // 检查任务状态
      if (bountyStatus !== "bidding") {
        reasons.push("任务不在开放竞标状态");
      }

      // 检查竞标截止时间
      if (biddingDeadline) {
        const deadlineDate = new Date(biddingDeadline);
        if (new Date() > deadlineDate) {
          reasons.push("竞标截止时间已过，无法提交竞标方案");
        }
      }

      // 不能认领自己的任务
      if (publisherId === userId) {
        reasons.push("不能认领自己发布的任务");
      }

      // 检查竞标人数上限
      if (maxBidders !== undefined && maxBidders > 0 && bidCount !== undefined && bidCount >= maxBidders) {
        reasons.push("竞标人数已达上限");
      }

      // 检查冷却期
      const supabase = getBrowserSupabaseClient();
      if (supabase) {
        const { data: abandonRecord } = await supabase
          .from("bounty_abandon_records")
          .select("cooldown_until")
          .eq("engineer_id", userId)
          .gt("cooldown_until", new Date().toISOString())
          .order("cooldown_until", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (abandonRecord) {
          reasons.push("您处于放弃冷却期内，暂时无法认领新任务");
        }
      }

      return { canClaim: reasons.length === 0, reasons };
    },
    enabled: !!bountyId && !!userId,
    staleTime: 30 * 1000,
  });
}
